import { Menu, Switch, Divider, Dropdown, notification, Button } from "antd";
import { useState, useEffect } from "react";
import {
  LogoutOutlined,
  SunOutlined,
  MoonOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  OrderedListOutlined,
  TransactionOutlined,
  WifiOutlined,
  DownOutlined,
  SnippetsOutlined,
  LoginOutlined,
  DollarCircleOutlined,
  PhoneOutlined,
  UserAddOutlined,
  GlobalOutlined,
  SettingOutlined,
  ClusterOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  UserSwitchOutlined,
  SearchOutlined,
  FileTextOutlined,
  PullRequestOutlined,
  SwapOutlined,
  LineChartOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { useTheme } from "../../store/context/theme-context";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useTranslation } from "react-i18next";
import { logoutUser } from "../../store/redux/actions/userActions";
import LanguageSwitcher from "../../shared/languages/LanguageSwitcher";
import { useLanguage } from "../../store/context/language-context";
import { languageMap } from "../../shared/languages/LanguageSelector";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { useTenant } from "../../store/context/tenant-context";
import {
  featuresEnum,
  PermissionsEnum,
  SelfUserClassificationEnum,
} from "../../shared/constants/constants";
import { useFeatures } from "../../store/context/feature-context";
import { navigateToAuth } from "../../utils/helper/redirect";
import { usePermissions } from "../../store/context/permission-context";

const Sidebar = ({ collapsed, type }) => {
  const { theme, toggleTheme } = useTheme();
  const tenant = useTenant();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { findFeature } = useFeatures();
  const { findPermission } = usePermissions();
  // console.log("permission from context-sidebar", permissions);
  const { language: languageCode, showModal } = useLanguage();
  const [sessionData, setSessionData] = useState(null);
  const [initials, setInitials] = useState("");

  const addFeatureMenuItem = (featureKeys, item, permissionKey) => {
    // If featureKeys is an array, check all features
    const checkFeatures = () => {
      if (Array.isArray(featureKeys)) {
        return featureKeys.every((key) => findFeature(key));
      }
      return findFeature(featureKeys);
    };

    const shouldRender = permissionKey
      ? checkFeatures() && findPermission(permissionKey)
      : checkFeatures();

    return shouldRender ? [item] : [];
  };

  useEffect(() => {
    const storedSessionData = getSessionData();
    if (storedSessionData) {
      setSessionData(storedSessionData);
      const firstNameInitial = (storedSessionData?.firstName ?? "")
        .charAt(0)
        .toUpperCase();
      const lastNameInitial = (storedSessionData?.lastName ?? "")
        .charAt(0)
        .toUpperCase();
      setInitials(`${firstNameInitial}${lastNameInitial}`);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logoutEvent") {
        navigateToAuth({ isAdmin: false });

        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [navigate]);

  const handleSignIn = () => {
    if (findFeature(featuresEnum.CUSTOMER_PORTAL))
      navigateToAuth({ isAdmin: false });
    else {
      //show a message to show that this feature is not available
      notification.info({ message: "Sign In feature not available." });
    }
  };

  const handleRegister = () => {
    if (findFeature(featuresEnum.CUSTOMER_PORTAL)) navigate("/segment");
    // if(findFeature(featuresEnum.ADMIN_PORTAL))
    else {
      //navigate("/authentication/admin/register");
      //show a message to show that this feature is not available
      notification.info({ message: "Registration feature not available." });
    }
  };

  const handleSignOut = () => {
    dispatch(logoutUser());
    sessionData?.userClassification ===
      SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
      ? navigateToAuth({ isAdmin: true })
      : navigateToAuth({ isAdmin: false });
  };

  const handleMenuClick = ({ key }) => {
    const item = items.find((item) => item.key === key); // Find the clicked item
    if (item) {
      if (item.path) {
        navigate(item.path); // Navigate to the path if it exists
      } else if (item.onClick) {
        item.onClick(); // Call the onClick handler if it exists
      }
    }
  };

  const ProfileDropdown = () => {
    const profileMenuItems = [
      {
        key: "profile",
        icon: <UserOutlined />,
        label: "My Profile",
      },
      {
        key: "settings",
        icon: <SettingOutlined />,
        label: "Settings",
      },
      // {
      //   key: "divider",
      //   type: "divider",
      // },
      // {
      //   key: "logout",
      //   icon: <LogoutOutlined />,
      //   label: "Logout",
      //   onClick: handleSignOut,
      // },
    ];

    const handleMenuClick = ({ key }) => {
      navigate(`/${key}`);
      switch (key) {
        case "profile":
          navigate("/settings/profile");
          break;
        case "settings":
          navigate("/settings");
          break;

        default:
          return;
      }
    };
    return (
      <Dropdown
        menu={{
          items: profileMenuItems,
          onClick: handleMenuClick,
          selectable: false,
        }}
        trigger={["click"]}
        placement="bottomRight"
        overlayStyle={{ marginTop: "15px" }}
      >
        <DownOutlined
          style={{ color: tenant.theme.colorPrimary }}
          className="cursor-pointer pointer-events-auto"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      </Dropdown>
    );
  };

  const profileName = `${sessionData?.displayName}` || "";
  // const accountType =
  //   sessionData?.accountType === 1
  //     ? "Personal"
  //     : sessionData?.accountType === 2 || sessionData?.accountType === 0
  //       ? "Corporate"
  //       : null;

  const languageName = languageMap[languageCode];
  const items = [
    {
      key: collapsed ? "collapsed" : "expanded",
      style: { height: "auto" },
      className: "non-interactive",
      label: (
        <div className="flex items-center justify-between">
          {!collapsed && (
            <>
              <img
                src={theme === "dark" ? tenant.darkLogo : tenant.logo}
                alt={tenant?.companyName}
                style={{ width: 90, height: 90, marginTop: 15 }}
              />
            </>
          )}
        </div>
      ),
    },
    ...(type === "portal"
      ? [
        {
          key: "divider1",
          width: "auto",
          label: <Divider />,
          className: "non-interactive",
        },
        {
          key: "home",
          icon: <HomeOutlined />,
          label: t("Home"),
          path: "/",
        },
        {
          key: "signin",
          icon: <LoginOutlined />,
          label: "Sign In",
          onClick: handleSignIn,
        },
        {
          key: "register",
          icon: <UserAddOutlined />,
          label: "Register",
          onClick: handleRegister,
        },
        ...addFeatureMenuItem(featuresEnum.PORTAL_CALL_BACK_REQUEST, {
          key: "confirm-details",
          icon: <PhoneOutlined />,
          label: t("Request a CallBack"),
          path: "/confirm-details",
        }),
        {
          key: "divider2",
          width: "auto",
          label: <Divider />,
          className: "non-interactive",
        },
        ...addFeatureMenuItem(featuresEnum.PORTAL_LANGUAGE_TOGGLE, {
          key: "english",
          icon: <GlobalOutlined onClick={showModal} />,
          label: (
            <div>
              <p
                type="text"
                onClick={showModal}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {languageName}
              </p>
              <LanguageSwitcher />
            </div>
          ),
        }),
        ...addFeatureMenuItem(featuresEnum.PORTAL_DARK_MODE_SWITCH, {
          key: "theme",
          icon:
            theme === "dark" ? (
              <MoonOutlined onClick={toggleTheme} />
            ) : (
              <SunOutlined onClick={toggleTheme} />
            ),
          label: (
            <div className="flex items-center justify-between">
              <span className="mr-2">Dark Mode</span>
              <Switch
                checked={theme === "dark"}
                onChange={toggleTheme}
                size="small"
              />
            </div>
          ),
        }),
        {
          key: "userswitch",
          icon:
            theme === "dark" ? (
              <UserSwitchOutlined />
            ) : (
              // the best ant design icon to use for switching admin is
              <UserSwitchOutlined />
            ),
          label: (
            <div className="flex items-center justify-between">
              <span className="mr-2">Admin Switch</span>
              <Switch
                // checked={theme === "dark"}
                onChange={() => navigateToAuth({ isAdmin: true })}
                size="small"
              />
            </div>
          ),
        },
      ]
      : []),
    ...(type === "inner"
      ? [
        {
          key: "profile",
          className: "non-interactive",
          style: { height: "auto" },
          label: !collapsed && (
            <div
              className={`${theme === "dark" ? "text-white bg-[#1d1d1d]" : "bg-[#f5f5f5]"
                } flex items-center justify-start px-1 py-3 pr-3 rounded-lg my-3 mx-0 gap-3 `}
            >
              <div
                className="flex items-center justify-center text-lg text-white rounded-full select-none size-11 min-w-11"
                style={{ backgroundColor: tenant.theme.colorPrimary }}
              >
                {initials || "PN"}
              </div>
              <div className="flex-1 h-12 text-base">
                <p>
                  <span className="block text-sm font-medium capitalize">
                    {profileName}
                  </span>

                  <span
                    className={`${theme === "dark" ? "text-[#8c8c8c]" : "text-[#595959]"
                      } text-sm block`}
                  >
                    {sessionData?.userClassificationDescription}
                  </span>
                </p>
              </div>

              {sessionData?.userClassification !==
                SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value && (<ProfileDropdown />)}
            </div>
          ),
        },
        ...(sessionData?.userClassification ===
          SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
          ? [
            findPermission(PermissionsEnum.ADM_DASH) && {
              key: "adminDashboard",
              icon: <HomeOutlined />,
              label: t("Dashboard"),
              path: "/admin-dashboard",
            },
            // findPermission(PermissionsEnum.ADM_SEARCH) &&
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_GENERAL_SEARCH,
              {
                key: "AdminGeneralSearch",
                icon: <SearchOutlined />,
                label: t("General Search"),
                path: "/general-search",
              },
              PermissionsEnum.ADM_SEARCH
            ),
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_CUSTOMERS_REPORT,
              {
                key: "AdminCustomers",
                icon: <UsergroupAddOutlined />,
                label: t("Customers"),
                path: "/customers-report",
              },
              PermissionsEnum.ADM_REPORT_CUST
            ),
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_LOAN_REQUESTS,
              {
                key: "AdminLoanRequests",
                icon: <FileTextOutlined />,
                label: t("Loan Requests"),
                path: "/loan-requests-report",
              },
              PermissionsEnum.ADM_REPORT_LOAN
            ),
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_TRANSACTIONS_REPORT,
              {
                key: "AdminTransactions",
                icon: <SnippetsOutlined />,
                label: t("Transactions"),
                path: "/transactions-report",
              },
              PermissionsEnum.ADM_REPORT_TRANS
            ),
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_TRANSFER_REQUESTS,
              {
                key: "AdminTransfers",
                icon: <TransactionOutlined />,
                label: t("Transfers"),
                path: "/transfers-report",
              },
              PermissionsEnum.ADM_REPORT_TRANSFER
            ),
            ...addFeatureMenuItem(
              [
                featuresEnum.ADMIN_NIBSS_MANDATE_SETUP,
                featuresEnum.ADMIN_NIBSS_MANDATE_COLLECTION,
              ],
              {
                key: "MandateManagment",
                icon: <FileTextOutlined />,
                label: t("Mandate Management"),
                path: "/mandate/selector",
              }
            ),
            ...addFeatureMenuItem(featuresEnum.ADMIN_MANAGE_CHANNELS, {
              key: "AdminGeneralRequests",
              icon: <OrderedListOutlined />,
              label: t("Manage Channels"),
              path: "/general-requests-selector",
            }),
            ...addFeatureMenuItem(featuresEnum.ADMIN_ORGANISATION_SETUP, {
              key: "AdminOrganisations",
              icon: <ClusterOutlined />,
              label: t("Organisations"),
              path: "/organisations",
            }),
            ...addFeatureMenuItem(
              featuresEnum.ADMIN_PRODUCT_CONFIGURATION,
              {
                key: "AdminProductConfiguration",
                icon: <SettingOutlined />,
                label: t("Product Configuration"),
                path: "/product-configuration",
              }
            ),
            {
              key: "AdminProcessInstance",
              icon: <SettingOutlined />,
              label: t("Process Instance"),
              path: "/ProcessInstance",
            }
          ]
          : [
            {
              key: "dashboard",
              icon: <HomeOutlined />,
              label: t("Dashboard"),
              path: "/dashboard",
            },
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_ACCOUNT_STATMENT, {
              key: "AccountStatement",
              icon: <FileTextOutlined />,
              label: t("Account Statement"),
              path: "/account-statement",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_TRANSACTIONS, {
              key: "Transactions",
              icon: <TransactionOutlined />,
              label: t("Transactions"),
              path: "/my-transactions",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_TRANSFER, {
              key: "Transfer",
              icon: <SwapOutlined />,
              label: t("Transfer"),
              path: "/transfer",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_AIRTIME_RECHARGE, {
              key: "AirtimeRecharge",
              icon: <PhoneOutlined />,
              label: t("Airtime Recharge"),
              path: "/airtime",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_DATA_PURCHASE, {
              key: "DataRecharge",
              icon: <WifiOutlined />,
              label: t("Data Recharge"),
              path: "/data",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_BILL, {
              key: "BillsPayment",
              icon: <MedicineBoxOutlined />,
              label: t("Bills Payment"),
              path: "/bills-payment",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_LOAN, {
              key: "loans",
              icon: <DollarCircleOutlined />,
              label: t("My Loans"),
              path: "/loans",
            }),
            ...addFeatureMenuItem(featuresEnum.CUSTOMER_SAVINGS_FEATURE, {
              key: "savings",
              icon: <LineChartOutlined />,
              label: t("My Savings"),
              path: "/my-savings",
            }),

            ...addFeatureMenuItem(featuresEnum.CUSTOMER_SAVINGS_FEATURE, {
              key: "merchantProfile",
              icon: <FileOutlined />,
              label: t("Subaccounts"),
              path: "/merchant-profile",
            }),

            {
              key: "Request",
              icon: <PullRequestOutlined />,
              label: t("Requests"),
              path: "/requests",
            },
          ]),
        {
          key: "divider3",
          width: "auto",
          label: <Divider />,
          className: "non-interactive",
        },
        {
          key: "settings",
          icon: <SettingOutlined />,
          label: t("Settings & More"),
          path: "/settings",
        },
        ...addFeatureMenuItem(featuresEnum.PORTAL_LANGUAGE_TOGGLE, {
          key: "english",
          icon: <GlobalOutlined />,
          label: (
            <div>
              <p
                type="text"
                onClick={showModal}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {languageName}
              </p>
              <LanguageSwitcher />
            </div>
          ),
        }),
        ...addFeatureMenuItem(featuresEnum.PORTAL_DARK_MODE_SWITCH, {
          key: "theme",
          icon:
            theme === "dark" ? (
              <MoonOutlined onClick={toggleTheme} />
            ) : (
              <SunOutlined onClick={toggleTheme} />
            ),
          label: (
            <div className="flex items-center justify-between">
              <span className="mr-2">Dark Mode</span>
              <Switch
                checked={theme === "dark"}
                onChange={toggleTheme}
                size="small"
              />
            </div>
          ),
        }),

        {
          key: "divider4",
          width: "auto",
          label: <Divider />,
          className: "non-interactive",
        },
        {
          key: "signout",
          icon: <LogoutOutlined />,
          label: "Sign Out",
          onClick: handleSignOut,
        },
      ]
      : []),
  ];

  // Find the active menu item based on the current URL
  const selectedKey =
    items.find((item) => location.pathname.startsWith(item?.path))?.key || "";

  return (
    <Menu
      onClick={handleMenuClick}
      selectedKeys={[selectedKey]}
      mode="inline"
      style={{
        height: "100vh",
        overflow: "auto",
      }}
      items={items}
    />
  );
};

export default Sidebar;

//
//
